/**
 * Identidade visual do cliente aplicada em runtime.
 *
 * A build e uma so para todos os clientes, entao logo, cores e nome nao podem
 * vir de variavel de ambiente (que congela em build time). Chegam da API e sao
 * escritos nas CSS variables que o Tailwind ja consome.
 */

import {
  tenantColorVars,
  type ThemeMode,
  type UserLanguage,
} from '@xtreed/academy-shared';

export interface TenantConfig {
  name: string;
  slug: string;
  status: string;
  logo: string | null;
  favicon: string | null;
  banner: string | null;
  themeColors: { primary: string | null; secondary: string | null };
  customCss: string | null;
  featureFlags: Record<string, unknown>;
  settings: Record<string, unknown>;
  defaultLanguage?: UserLanguage;
  themeMode?: ThemeMode;
}

const STYLE_ELEMENT_ID = 'tenant-custom-css';

export function applyTenantTheme(config: TenantConfig): void {
  const root = document.documentElement;
  const vars = tenantColorVars(config.themeColors ?? {});
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }

  if (config.name) {
    document.title = config.name;
  }

  if (config.favicon) {
    let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      document.head.appendChild(icon);
    }
    icon.href = config.favicon;
  }

  // CSS do cliente por ultimo, para poder sobrepor os tokens acima.
  const previous = document.getElementById(STYLE_ELEMENT_ID);
  if (previous) previous.remove();
  if (config.customCss) {
    const style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = config.customCss;
    document.head.appendChild(style);
  }
}

const CACHE_KEY = 'xt:tenant-config';

export function readCachedTenantConfig(): TenantConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as TenantConfig) : null;
  } catch {
    return null;
  }
}

/** Usado ao cair na plataforma: sem cliente, nao pode sobrar marca em cache. */
export function clearCachedTenantConfig(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Mesmo motivo do cacheTenantConfig: storage indisponivel nao quebra o boot.
  }
}

export function cacheTenantConfig(config: TenantConfig): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch {
    // Navegador anonimo ou storage cheio: o tema so deixa de ser instantaneo
    // na proxima visita, o que nao justifica quebrar o boot.
  }
}

export function hslPartsToHex(parts: string): string | null {
  const match = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(parts);
  if (!match) return null;
  const hue = Number(match[1]) % 360;
  const saturation = Math.min(100, Number(match[2])) / 100;
  const lightness = Math.min(100, Number(match[3])) / 100;
  const amount = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n: number) => {
    const k = (n + hue / 30) % 12;
    const value = lightness - amount * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(value * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
}

export function currentPrimaryHex(): string | null {
  return hslPartsToHex(
    getComputedStyle(document.documentElement).getPropertyValue('--primary'),
  );
}
